import { useState } from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blog/ui";

import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import type { TaxonomyKindPath, TaxonomyTerm } from "../lib/api";
import { t } from "../lib/i18n";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Category and tag management (SPEC §42/§43).
 *
 * The vocabulary is derived from content: publishing or syncing an article
 * with a `category:` or `tags:` frontmatter creates the terms here. This page
 * is where their display names are curated, and where a term nobody uses
 * anymore can be removed — deleting one that content still references is
 * refused, because the next sync would just bring it back.
 */
export function Taxonomy() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);

  if (siteLoading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (site === null) {
    return (
      <Card>
        <CardContent>{t("themes.noSite")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <PageHeader title={t("nav.taxonomy")} description={t("taxonomy.desc")} />

      <div className="grid gap-4 lg:grid-cols-2">
        <TermManager kind="categories" title={t("taxonomy.categoriesTitle")} siteId={site.id} />
        <TermManager kind="tags" title={t("taxonomy.tagsTitle")} siteId={site.id} />
      </div>
    </>
  );
}

function TermManager({
  kind,
  title,
  siteId,
}: {
  readonly kind: TaxonomyKindPath;
  readonly title: string;
  readonly siteId: string;
}) {
  const client = useApi();
  const terms = useAsync(async () => await client.listTaxonomy(siteId, kind), [siteId, kind]);

  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(): Promise<void> {
    if (slug.trim() === "") {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await client.createTerm(siteId, kind, {
        slug: slug.trim(),
        ...(name.trim() === "" ? {} : { name: name.trim() }),
      });

      setSlug("");
      setName("");
      terms.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("github.syncFailed"));
    } finally {
      setBusy(false);
    }
  }

  const field =
    "h-9 w-full rounded-md border border-neutral-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {t("taxonomy.termCount", { count: terms.data?.length ?? 0 })}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <input
            className={field}
            placeholder={t("taxonomy.newSlug")}
            aria-label={`${title} — ${t("taxonomy.slug")}`}
            value={slug}
            onChange={(event) => {
              setSlug(event.target.value);
            }}
          />
          <input
            className={field}
            placeholder={t("taxonomy.newName")}
            aria-label={`${title} — ${t("taxonomy.name")}`}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <Button type="submit" size="sm" loading={busy} disabled={slug.trim() === ""}>
            {t("taxonomy.add")}
          </Button>
        </form>

        {error !== null && <p className="text-sm text-red-600">{error}</p>}
        {terms.error !== null && <p className="text-sm text-red-600">{terms.error}</p>}

        {terms.data !== null && terms.data.length === 0 && (
          <p className="text-sm text-neutral-500">{t("taxonomy.empty")}</p>
        )}

        <ul className="flex flex-col gap-2">
          {(terms.data ?? []).map((term) => (
            <TermRow key={term.id} kind={kind} term={term} siteId={siteId} onChanged={terms.reload} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function TermRow({
  kind,
  term,
  siteId,
  onChanged,
}: {
  readonly kind: TaxonomyKindPath;
  readonly term: TaxonomyTerm;
  readonly siteId: string;
  readonly onChanged: () => void;
}) {
  const client = useApi();

  const [name, setName] = useState(term.name);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      await client.updateTerm(siteId, kind, term.id, { name: name.trim() });
      setMessage(t("taxonomy.saved"));
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("github.syncFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      await client.deleteTerm(siteId, kind, term.id);
      setMessage(t("taxonomy.deleted"));
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("github.syncFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="flex flex-col gap-1 rounded-md border border-neutral-200 p-2">
      <div className="flex items-center gap-2">
        <code className="shrink-0 text-xs text-neutral-500">{term.slug}</code>
        <Badge variant="outline">
          {t("taxonomy.used")} {term.count}
        </Badge>

        <span className="flex-1" />

        <input
          className="h-8 w-40 rounded-md border border-neutral-300 bg-white px-2 text-sm"
          aria-label={`${term.slug} — ${t("taxonomy.name")}`}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <Button
          size="sm"
          variant="outline"
          loading={busy}
          disabled={name.trim() === "" || name === term.name}
          onClick={() => void save()}
        >
          {t("taxonomy.save")}
        </Button>
        <Button size="sm" variant="destructive" disabled={busy} onClick={() => void remove()}>
          {t("taxonomy.delete")}
        </Button>
      </div>

      {message !== null && <p className="text-xs text-green-700">{message}</p>}
      {error !== null && <p className="text-xs text-red-600">{error}</p>}
    </li>
  );
}